import * as React from 'react';
const { useState, useEffect } = React;

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
}

export const usePWAInstall = () => {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [userEngaged, setUserEngaged] = useState(false);

  useEffect(() => {
    // Check if already installed
    const checkIfInstalled = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
      const isInWebAppiOS = !!(window.navigator as any).standalone;
      const installed = isStandalone || isInWebAppiOS;
      setIsInstalled(installed);
      console.log('PWA Install Check:', { isStandalone, isInWebAppiOS, installed });
    };

    checkIfInstalled();

    // Enhanced user engagement tracking for Android PWA installation
    const trackUserEngagement = () => {
      setUserEngaged(true);
      console.log('PWA: User engagement detected');
      
      // Store engagement for Android PWA criteria
      localStorage.setItem('pwa-user-engaged', 'true');
      localStorage.setItem('pwa-engagement-time', Date.now().toString());
    };

    // More comprehensive engagement tracking for Android
    const engagementEvents = ['click', 'keydown', 'scroll', 'touchstart', 'pointerdown'];
    const timeoutId = setTimeout(() => {
      // Time-based engagement (30 seconds on site)
      trackUserEngagement();
    }, 30000);

    engagementEvents.forEach(event => {
      document.addEventListener(event, trackUserEngagement, { once: true, passive: true });
    });

    // Track multiple visits for Android PWA criteria
    const visitCount = parseInt(localStorage.getItem('pwa-visit-count') || '0') + 1;
    localStorage.setItem('pwa-visit-count', visitCount.toString());
    localStorage.setItem('pwa-last-visit', Date.now().toString());
    
    if (visitCount >= 2) {
      console.log('PWA: Multiple visits detected, improving install eligibility');
    }

    // Listen for install prompt
    const handleBeforeInstallPrompt = (e: Event) => {
      console.log('PWA beforeinstallprompt event fired!', e);
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);
      setIsInstallable(true);
      console.log('PWA install prompt available');
    };

    // Listen for app installed
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setInstallPrompt(null);
      setIsInstallable(false);
      console.log('PWA installed successfully');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // PWA Installability check - only use native browser prompt
    const checkInstallability = () => {
      console.log('PWA Installability Check:', {
        hasPrompt: !!installPrompt,
        isInstalled,
        userEngaged,
        location: window.location.hostname
      });
      
      // Only set installable if we have a real browser prompt
      // This prevents false positives and ensures accurate detection
      if (installPrompt && !isInstalled) {
        console.log('PWA: Setting installable based on native browser prompt');
        setIsInstallable(true);
      } else {
        setIsInstallable(false);
      }
    };

    checkInstallability();

    // Cleanup engagement listeners
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      engagementEvents.forEach(event => {
        document.removeEventListener(event, trackUserEngagement);
      });
    };
  }, [isInstalled, userEngaged, installPrompt]);

  const triggerInstall = async () => {
    if (!installPrompt) {
      // Provide manual installation instructions
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      if (isMobile) {
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        const isAndroid = /Android/.test(navigator.userAgent);
        
        let message = 'To install this app:\n\n';
        if (isIOS) {
          message += '1. Tap the Share button (⇧) in Safari\n2. Scroll down and tap "Add to Home Screen"\n3. Tap "Add" to confirm';
        } else if (isAndroid) {
          message += '1. Tap the menu (⋮) in your browser\n2. Select "Add to Home screen" or "Install app"\n3. Tap "Add" to confirm';
        } else {
          message += '1. Look for the install icon in your browser\'s address bar\n2. Or check your browser\'s menu for "Install" option';
        }
        
        alert(message);
        return true;
      } else {
        // Desktop - provide browser-specific instructions
        const isChrome = /Chrome/.test(navigator.userAgent);
        const isEdge = /Edg/.test(navigator.userAgent);
        const isFirefox = /Firefox/.test(navigator.userAgent);
        
        let message = 'To install this app:\n\n';
        if (isChrome || isEdge) {
          message += '1. Look for the install icon (⊕) in the address bar\n2. Or click the menu (⋯) → "Install LamuLamu..."\n3. Click "Install" to confirm';
        } else if (isFirefox) {
          message += '1. This app can be installed on Chrome, Edge, or mobile browsers\n2. Firefox doesn\'t currently support PWA installation';
        } else {
          message += '1. This app can be installed on Chrome, Edge, or mobile browsers\n2. Look for install options in your browser menu';
        }
        
        alert(message);
        return true;
      }
    }

    try {
      await installPrompt.prompt();
      const choiceResult = await installPrompt.userChoice;
      
      if (choiceResult.outcome === 'accepted') {
        console.log('User accepted PWA install');
        setInstallPrompt(null);
        setIsInstallable(false);
        return true;
      } else {
        console.log('User dismissed PWA install');
        return false;
      }
    } catch (error) {
      console.error('Error during PWA install:', error);
      return false;
    }
  };

  // Debug logging
  console.log('PWA Hook State:', { 
    isInstallable, 
    isInstalled, 
    finalInstallable: isInstallable && !isInstalled,
    hasPrompt: !!installPrompt 
  });

  return {
    isInstallable: isInstallable && !isInstalled,
    isInstalled,
    triggerInstall
  };
};